import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAdminCompanies, createAdminCompany } from '../../services/adminService';
import './AdminPage.css';

const STATUS_LABELS = { trial: 'Trial', active: 'Ativo', suspended: 'Suspenso', cancelled: 'Cancelado' };
const BILLING_CYCLE_LABELS = { monthly: 'Mensal', quarterly: 'Trimestral', annual: 'Anual', custom: 'Personalizado' };

function maskCpfCnpj(value) {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function StatusBadge({ status }) {
  return <span className={`adm-badge adm-badge-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

const EMPTY_FORM = {
  name: '', document_number: '', contact_name: '', contact_email: '', contact_phone: '',
  address: '', notes: '', active: true,
  plan_name: 'Plano Pro', price: '', billing_cycle: 'monthly',
  subscription_status: 'trial', trial_ends_at: '', next_billing_date: '', max_users: '',
};

export default function AdminCompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    getAdminCompanies(params)
      .then(data => {
        setCompanies(data.data || []);
        setMeta({ current_page: data.current_page, last_page: data.last_page, total: data.total });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = name === 'document_number'
      ? maskCpfCnpj(value)
      : (type === 'checkbox' ? checked : value);
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const payload = { ...form };
      if (!payload.price) { setFormError('Informe o valor do plano.'); setSaving(false); return; }
      payload.price = parseFloat(payload.price);
      if (payload.max_users) payload.max_users = parseInt(payload.max_users);
      const result = await createAdminCompany(payload);
      setShowModal(false);
      setForm(EMPTY_FORM);
      setPage(1);
      load();
      if (result?.welcome_email_sent) {
        setSuccessMsg(`Empresa criada com sucesso! E-mail de boas-vindas enviado para ${payload.contact_email}.`);
      } else {
        setSuccessMsg('Empresa criada com sucesso!');
      }
      setTimeout(() => setSuccessMsg(null), 8000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao criar empresa.';
      const errors = err.response?.data?.errors;
      if (errors) {
        setFormError(Object.values(errors).flat().join(' '));
      } else {
        setFormError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <div className="adm-breadcrumb"><Link to="/admin">Dashboard</Link> / Empresas</div>
          <h2>Empresas ({meta.total})</h2>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={() => setShowModal(true)}>
          + Nova Empresa
        </button>
      </div>

      {successMsg && (
        <div className="adm-alert" style={{ background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46' }}>
          ✓ {successMsg}
        </div>
      )}

      {/* Toolbar */}
      <form className="adm-toolbar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Buscar por nome, e-mail ou CNPJ..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Todos os status</option>
          <option value="trial">Trial</option>
          <option value="active">Ativo</option>
          <option value="suspended">Suspenso</option>
          <option value="cancelled">Cancelado</option>
        </select>
        <button type="submit" className="adm-btn adm-btn-secondary">Buscar</button>
      </form>

      {/* Table */}
      {loading ? (
        <div className="adm-loading">Carregando...</div>
      ) : companies.length === 0 ? (
        <div className="adm-empty">
          <div className="adm-empty-icon">🏢</div>
          <p>Nenhuma empresa encontrada.</p>
        </div>
      ) : (
        <div className="adm-card" style={{ padding: 0 }}>
          <div className="adm-table-wrapper">
            <table className="adm-table adm-table--cards">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>CNPJ</th>
                  <th>Plano</th>
                  <th>Valor</th>
                  <th>Ciclo</th>
                  <th>Status</th>
                  <th>Próx. Cobrança</th>
                  <th>Usuários</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(c => {
                  const sub = c.subscription;
                  return (
                    <tr
                      key={c.id}
                      className="clickable"
                      onClick={() => navigate(`/admin/companies/${c.id}`)}
                    >
                      <td data-label="Empresa" style={{ fontWeight: 500, color: '#4f46e5' }}>{c.name}</td>
                      <td data-label="CNPJ" data-hide-mobile="true" style={{ color: '#6b7280', fontFamily: 'monospace' }}>{c.document_number || '—'}</td>
                      <td data-label="Plano" data-hide-mobile="true">{sub?.plan_name || '—'}</td>
                      <td data-label="Valor">{sub ? formatCurrency(sub.price) : '—'}</td>
                      <td data-label="Ciclo" data-hide-mobile="true">{sub ? BILLING_CYCLE_LABELS[sub.billing_cycle] || sub.billing_cycle : '—'}</td>
                      <td data-label="Status"><StatusBadge status={sub?.status || 'trial'} /></td>
                      <td data-label="Próx. Cobrança" data-hide-mobile="true">{formatDate(sub?.next_billing_date)}</td>
                      <td data-label="Usuários" style={{ textAlign: 'center' }}>{c.users_count ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="adm-pagination" style={{ padding: '0.75rem 1rem' }}>
            <span>Página {meta.current_page} de {meta.last_page}</span>
            <button disabled={meta.current_page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</button>
            <button disabled={meta.current_page >= meta.last_page} onClick={() => setPage(p => p + 1)}>Próxima →</button>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      {showModal && (
        <div className="adm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>Nova Empresa</h3>
              <button className="adm-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="adm-modal-body">
                {formError && <div className="adm-alert adm-alert-danger">{formError}</div>}
                <div style={{ fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>Dados da Empresa</div>
                <div className="adm-form-grid">
                  <div className="adm-form-group">
                    <label>Nome *</label>
                    <input name="name" value={form.name} onChange={handleFormChange} required />
                  </div>
                  <div className="adm-form-group">
                    <label>CNPJ/CPF *</label>
                    <input
                      name="document_number"
                      value={form.document_number}
                      onChange={handleFormChange}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      required
                    />
                  </div>
                  <div className="adm-form-group">
                    <label>Contato</label>
                    <input name="contact_name" value={form.contact_name} onChange={handleFormChange} placeholder="Nome do contato" />
                  </div>
                  <div className="adm-form-group">
                    <label>E-mail do contato</label>
                    <input name="contact_email" type="email" value={form.contact_email} onChange={handleFormChange} />
                  </div>
                  <div className="adm-form-group">
                    <label>Telefone</label>
                    <input name="contact_phone" value={form.contact_phone} onChange={handleFormChange} />
                  </div>
                  <div className="adm-form-group">
                    <label>Endereço</label>
                    <input name="address" value={form.address} onChange={handleFormChange} />
                  </div>
                </div>

                <div style={{ fontWeight: 600, margin: '1rem 0 0.75rem', color: '#374151' }}>Assinatura Inicial</div>
                <div className="adm-form-grid">
                  <div className="adm-form-group">
                    <label>Plano *</label>
                    <input name="plan_name" value={form.plan_name} onChange={handleFormChange} required />
                  </div>
                  <div className="adm-form-group">
                    <label>Valor Mensal (R$) *</label>
                    <input name="price" type="number" step="0.01" min="0" value={form.price} onChange={handleFormChange} required />
                  </div>
                  <div className="adm-form-group">
                    <label>Ciclo de Cobrança</label>
                    <select name="billing_cycle" value={form.billing_cycle} onChange={handleFormChange}>
                      <option value="monthly">Mensal</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="annual">Anual</option>
                      <option value="custom">Personalizado</option>
                    </select>
                  </div>
                  <div className="adm-form-group">
                    <label>Status</label>
                    <select name="subscription_status" value={form.subscription_status} onChange={handleFormChange}>
                      <option value="trial">Trial</option>
                      <option value="active">Ativo</option>
                      <option value="suspended">Suspenso</option>
                    </select>
                  </div>
                  <div className="adm-form-group">
                    <label>Fim do Trial</label>
                    <input name="trial_ends_at" type="date" value={form.trial_ends_at} onChange={handleFormChange} />
                  </div>
                  <div className="adm-form-group">
                    <label>Próxima Cobrança</label>
                    <input name="next_billing_date" type="date" value={form.next_billing_date} onChange={handleFormChange} />
                  </div>
                  <div className="adm-form-group">
                    <label>Máx. Usuários</label>
                    <input name="max_users" type="number" min="1" value={form.max_users} onChange={handleFormChange} />
                  </div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Criar Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
